import React, { useEffect, useState } from 'react'
import { Button, Theme, Dialog, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/styles';
import { anime } from 'react-anime';
import { Link, useHistory } from 'react-router-dom';
import { appName } from '../util/util'
import axios from 'axios';
import CreateDialogContent from './join/CreateDialogContent';
import JoinDialogContent from './join/JoinDialogContent';
import { RoomProperties } from '../models/ServerModels';

export const appVersion = "3.0.0"

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
    },
    hero: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '0 64px',
        boxSizing: 'border-box',
        transform: 'translateY(-36px)',
        [theme.breakpoints.down('sm')]: {
            padding: '0 24px',
            transform: 'translateY(-28px)',
        },
    },
    cluster: {
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        gridTemplateRows: 'auto auto',
        columnGap: 40,
        alignItems: 'start',
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            alignItems: 'center',
            gap: 28,
        },
    },
    logoStage: {
        position: 'relative',
        width: 120,
        gridColumn: 1,
        gridRow: 1,
        alignSelf: 'stretch',
        overflow: 'visible',
        [theme.breakpoints.down('sm')]: {
            width: 200,
            height: 150,
            alignSelf: 'center',
        },
    },
    logoStack: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 80,
        height: 120,
        marginLeft: -40,
        transform: 'translateY(-50%)',
        [theme.breakpoints.down('sm')]: {
            top: 0,
            transform: 'none',
        },
    },
    copy: {
        display: 'contents',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            marginTop: 8,
        },
    },
    headlines: {
        gridColumn: 2,
        gridRow: 1,
    },
    title: {
        margin: 0,
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
            lineHeight: 1.2,
        },
    },
    subtitle: {
        margin: 0,
        marginTop: 4,
        [theme.breakpoints.down('sm')]: {
            fontSize: '1.05rem',
            lineHeight: 1.3,
        },
    },
    buttons: {
        gridColumn: 2,
        gridRow: 2,
        marginTop: 16,
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'center',
        },
    },
    version: {
        position: 'absolute',
        left: 24,
        bottom: 24,
        [theme.breakpoints.down('sm')]: {
            left: 0,
            right: 0,
            textAlign: 'center',
            bottom: 16,
            paddingBottom: 'env(safe-area-inset-bottom)',
        },
    },
}))

export default function Home() {
    const theme = useTheme<Theme>()
    const classes = useStyles()
    const [showCreate, setShowCreate] = useState(false)
    const [showJoin, setShowJoin] = useState(false)
    const history = useHistory();

    useEffect(() => {
        document.title = appName

        let timeline = anime.timeline({
            targets: ".animCardLogoFront",
            easing: 'easeOutElastic'
        })

        timeline.add({
            duration: 500,
            delay: 100,
            scale: [0, 1],
        })
        timeline.add({
            borderRadius: [48, 8],
            top: "0%",
            height: [80, 120],
            duration: 600,
        })
        timeline.add({
            targets: ".animCardLogoMiddle, .animCardLogoBack",
            delay: 0,
            opacity: 1,
            duration: 10
        })
        timeline.add({
            duration: 800,
            rotate: 25,
            translateX: 32,
            translateY: 8
        })
        timeline.add({
            targets: ".animCardLogoBack",
            duration: 800,
            delay: 0,
            rotate: -25,
            translateX: -40,
        }, "-=750")

        timeline.add({
            targets: ".animAppTitle, .animAppSubtitle, .animButtons",
            delay: anime.stagger(50),
            translateX: [-50, 0],
            duration: 1200,
            opacity: 1,
        }, "-=650")
    }, [])

    function handleCreateClicked() {
        setShowCreate(true)
    }

    function handleCreateCancelled() {
        setShowCreate(false)
    }

    function handleCreateSubmit(properties: RoomProperties) {
        axios.get('/api/create', { params: properties })
            .then((res) => {
                if (res.data.status === "success" && res.data.id != null) {
                    history.push(`/room/${res.data.id}`)
                } else {
                    console.error("Create failed")
                }
            }).catch((error) => {
                console.error(error)
            });
    }

    function handleJoinClicked() {
        setShowJoin(true)
    }

    function handleJoinCancelled() {
        setShowJoin(false)
    }

    function handleJoinSubmit(id: string) {
        history.push(`/room/${id}`)
    }

    return (
        <div className={classes.root}>
            <div className={classes.hero}>
                <div className={classes.cluster}>

                <div className={classes.logoStage}>
                    <div className={classes.logoStack}>
                        <div className="animCardLogoFront logoCard" style={{ position: "absolute", top: "25%", width: "80px", height: "80px", backgroundColor: theme.palette.primary.main, borderRadius: "48px", zIndex: 3 }} />
                        <div className="animCardLogoMiddle logoCard" style={{ opacity: "0", backgroundColor: theme.palette.primary.main, filter: "brightness(70%)", zIndex: 2 }} />
                        <div className="animCardLogoBack logoCard" style={{ opacity: "0", backgroundColor: theme.palette.primary.main, filter: "brightness(50%)", zIndex: 1 }} />
                    </div>
                </div>

                <div className={classes.copy}>
                    <div className={classes.headlines}>
                        <Typography className={`animAppTitle ${classes.title}`} variant="h3" style={{ opacity: "0" }}>
                            {appName}
                        </Typography>
                        <Typography className={`animAppSubtitle ${classes.subtitle}`} variant="h5" style={{ opacity: "0" }}>
                            Planning poker for Agile development
                        </Typography>
                    </div>

                    <div className={`animButtons ${classes.buttons}`} style={{ opacity: "0" }} >
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ marginRight: 24, minWidth: 104 }}
                            onClick={handleCreateClicked}>
                            Create
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            style={{ minWidth: 104 }}
                            onClick={handleJoinClicked}>
                            Join
                        </Button>
                    </div>
                </div>
                </div>


                <Dialog fullWidth={true} open={showCreate} onClose={handleCreateCancelled} aria-labelledby="form-dialog-title">
                    <CreateDialogContent onCancel={handleCreateCancelled} onCreate={handleCreateSubmit} existingProperties={null} />
                </Dialog>
                <Dialog fullWidth={true} open={showJoin} onClose={handleJoinCancelled} aria-labelledby="form-dialog-title">
                    <JoinDialogContent onCancel={handleJoinCancelled} onJoin={handleJoinSubmit} />
                </Dialog>
            </div>
            <div className={classes.version}>
                <Link to={"/changelog"} style={{ textDecoration: "none", color: "inherit" }}>
                    <Typography variant="h6" style={{ fontSize: "16px" }}>
                        {`Version ${appVersion}`}
                    </Typography>
                </Link>
            </div>
        </div>
    )
}
